import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Save } from 'lucide-react';
import { TextField, Toggle } from '@/components/admin/shared';

export default function SettingsManager() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [engineEnabled, setEngineEnabled] = useState(null);
  const [engineSaving, setEngineSaving] = useState(false);
  const [engineMsg, setEngineMsg] = useState(null);

  const load = async () => {
    try { const { data } = await api.get('/settings'); setState(data); } catch (e) {}
    try { const { data } = await api.get('/planetsync'); setEngineEnabled(!!data?.enabled); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const toggleEngine = async (v) => {
    setEngineEnabled(v);
    setEngineSaving(true); setEngineMsg(null);
    try { await api.put('/admin/planetsync', { enabled: v }); setEngineMsg(v ? 'Atmosphere ON.' : 'Atmosphere OFF site-wide.'); }
    catch (e) { setEngineMsg(e?.response?.data?.detail || 'Toggle failed'); setEngineEnabled(!v); }
    finally { setEngineSaving(false); }
  };

  if (!state) return <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Loading site settings…</div>;

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const updateSocial = (k, v) => setState((s) => ({ ...s, social_links: { ...(s.social_links || {}), [k]: v } }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await api.put('/admin/settings', state);
      setState(data);
      setMsg('Saved.');
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-manager">
      <div className="pg-card p-6">
        <p className="pg-label">SITE BRAND</p>
        <h3 className="font-display text-2xl mt-1 tracking-[0.04em]">IDENTITY</h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Site title" value={state.site_title} onChange={(v) => update('site_title', v)} />
          <TextField label="Tagline" value={state.tagline} onChange={(v) => update('tagline', v)} />
        </div>
        <div className="mt-4">
          <TextField label="Footer copy (optional override)" value={state.footer_copy || ''} onChange={(v) => update('footer_copy', v)} textarea rows={2} />
        </div>
      </div>

      <div className="pg-card p-6">
        <p className="pg-label">TYPOGRAPHY + 3D COLLAB</p>
        <h3 className="font-display text-2xl mt-1 tracking-[0.04em]">CHANNELS, CAPS &amp; COLLAB SIGNATURE</h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Toggle label="ALL CAPS site-wide" value={state.all_caps_enabled} onChange={(v) => update('all_caps_enabled', v)} />
          <Toggle label="Floating Collab typography" value={state.collab_typography_enabled} onChange={(v) => update('collab_typography_enabled', v)} />
          <TextField label="Collab typography text" value={state.collab_typography_text} onChange={(v) => update('collab_typography_text', v)} />
        </div>
      </div>

      <div className="pg-card p-6">
        <p className="pg-label">SOCIAL CONTACTS</p>
        <h3 className="font-display text-2xl mt-1 tracking-[0.04em]">REACHABLE</h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Instagram URL" value={state.social_links?.instagram || ''} onChange={(v) => updateSocial('instagram', v)} />
          <TextField label="Spotify URL" value={state.social_links?.spotify || ''} onChange={(v) => updateSocial('spotify', v)} />
          <TextField label="YouTube URL" value={state.social_links?.youtube || ''} onChange={(v) => updateSocial('youtube', v)} />
          <TextField label="Email" value={state.social_links?.email || ''} onChange={(v) => updateSocial('email', v)} />
        </div>
      </div>

      <div className="pg-card p-6" data-testid="site-settings-world-engine">
        <p className="pg-label">ATMOSPHERE / WORLD ENGINE</p>
        <h3 className="font-display text-2xl mt-1 tracking-[0.04em]">BACKGROUND ATMOSPHERE</h3>
        <p className="text-sm text-[var(--pg-ink-muted)] mt-2 max-w-2xl">Master switch for the World Engine backdrop, palette, and time-of-day tinting. Turning this off hides all atmospheric layers across every page (site-wide). Fine-grain controls live under <span className="font-mono text-[var(--pg-ink)]">World Engine</span>.</p>
        <div className="mt-5 max-w-sm">
          {engineEnabled === null ? (
            <p className="text-xs font-mono text-[var(--pg-ink-faint)] tracking-widest">Loading atmosphere state…</p>
          ) : (
            <Toggle
              label={engineEnabled ? 'World Engine atmosphere: ON' : 'World Engine atmosphere: OFF'}
              value={!!engineEnabled}
              onChange={(v) => toggleEngine(v)}
            />
          )}
          {engineSaving && <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] mt-2 tracking-widest">SAVING…</p>}
          {engineMsg && <p className="text-[11px] font-mono text-[var(--pg-tertiary)] mt-2 tracking-widest">{engineMsg}</p>}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="pg-btn-primary w-full justify-center !py-3 disabled:opacity-60 inline-flex items-center gap-2" data-testid="settings-save">
        <Save size={14} /> {saving ? 'SAVING…' : 'PUBLISH SETTINGS'}
      </button>
      {msg && <p className="text-xs font-mono text-[var(--pg-tertiary)] text-center">{msg}</p>}
    </div>
  );
}
